import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Video, 
  ArrowLeft,
  Trophy,
  Coins,
  CheckCircle2,
  Lock,
  Zap,
  Flame,
  Diamond,
  Crown,
  Sparkles,
  Gift,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { UserModelData, PAGE_NAME } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { actions } from "../../redux";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";
import { THEME_COLORS } from "../../themes/DarkLight";

const DAILY_AD_LIMIT = 30;

// Escalating reward tiers
const TIERS = [
  { name: "Bronze", range: [1, 5], reward: 0.5, icon: <Coins size={14} />, color: "amber", emoji: "🥉" },
  { name: "Silver", range: [6, 10], reward: 0.75, icon: <Zap size={14} />, color: "slate", emoji: "🥈" },
  { name: "Gold", range: [11, 15], reward: 1.0, icon: <Crown size={14} />, color: "yellow", emoji: "🥇" },
  { name: "Diamond", range: [16, 20], reward: 1.5, icon: <Diamond size={14} />, color: "cyan", emoji: "💎" },
  { name: "Platinum", range: [21, 25], reward: 2.0, icon: <Crown size={14} />, color: "purple", emoji: "👑" },
  { name: "Legendary", range: [26, 30], reward: 2.5, icon: <Sparkles size={14} />, color: "rose", emoji: "🔥" },
];

const COMPLETION_BONUS = 10.0;

function getCurrentTier(adsWatched: number) {
  for (const tier of TIERS) {
    if (adsWatched < tier.range[1]) return tier;
  }
  return TIERS[TIERS.length - 1];
}

function getRewardForAd(adNumber: number): number {
  for (const tier of TIERS) {
    if (adNumber >= tier.range[0] && adNumber <= tier.range[1]) return tier.reward;
  }
  return 0.5;
}

function getTotalPotential(adsWatched: number): number {
  let total = 0;
  for (let i = adsWatched + 1; i <= DAILY_AD_LIMIT; i++) {
    total += getRewardForAd(i);
  }
  if (adsWatched < DAILY_AD_LIMIT) total += COMPLETION_BONUS;
  return total;
}

function getTotalEarned(adsWatched: number): number {
  let total = 0;
  for (let i = 1; i <= adsWatched; i++) {
    total += getRewardForAd(i);
  }
  return total;
}

const WatchAndEarnPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useThemeHook();
  const isDark = theme === "dark";
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [adsWatchedToday, setAdsWatchedToday] = useState(0);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [showRewardAnimation, setShowRewardAnimation] = useState(false);
  const [lastRewardAmount, setLastRewardAmount] = useState(0);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const savedData = localStorage.getItem(`watchAndEarn_${user?.id}_${today}`);
    
    if (savedData) {
      setAdsWatchedToday(parseInt(savedData, 10));
    } else {
      setAdsWatchedToday(0);
    }
  }, [user?.id]);

  const saveAdWatched = () => {
    const today = new Date().toISOString().split('T')[0];
    const newCount = adsWatchedToday + 1;
    setAdsWatchedToday(newCount);
    localStorage.setItem(`watchAndEarn_${user?.id}_${today}`, newCount.toString());
  };

  const handleWatchAd = async () => {
    if (isWatchingAd || adsWatchedToday >= DAILY_AD_LIMIT) return;
    
    const reward = getRewardForAd(adsWatchedToday + 1);
    
    setIsWatchingAd(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user?.id, async () => {
        saveAdWatched();
        
        await arthereumProvider.awardGameATH(user?.id || "", "watch_and_earn", reward, 1);
        
        dispatch(actions.setActualUser({
          ...userData,
          user: { 
            ...user, 
            arthereumBalance: (user?.arthereumBalance || 0) + reward 
          },
          miningInfos: userData.miningInfos,
        }));
        
        setLastRewardAmount(reward);
        setShowRewardAnimation(true);
        setTimeout(() => setShowRewardAnimation(false), 3000);
      });
    } catch (error: any) {
      console.error("Watch & Earn Ad failed:", error);
    } finally {
      setIsWatchingAd(false);
    }
  };

  const isLimitReached = adsWatchedToday >= DAILY_AD_LIMIT;
  const currentTier = getCurrentTier(adsWatchedToday);
  const currentReward = getRewardForAd(adsWatchedToday + 1);
  const totalEarned = getTotalEarned(adsWatchedToday);
  const totalPotential = getTotalPotential(adsWatchedToday);

  return (
    <div className={`min-h-screen pb-24 ${isDark ? "bg-slate-900" : "bg-gray-50"}`}>
      <CommonPageTitle title="Watch & Earn" padding={null} />

      <div className="max-w-lg mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)}
              className={`p-2 rounded-xl transition-colors ${isDark ? "bg-slate-800 hover:bg-slate-700" : "bg-white hover:bg-gray-100 shadow-sm"}`}
            >
              <ArrowLeft size={20} className={isDark ? "text-white" : "text-gray-900"} />
            </button>
            <div>
              <h1 className={`text-xl font-black ${isDark ? "text-white" : "text-gray-900"}`}>
                Ad Hub 📺
              </h1>
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-full ${isDark ? "bg-amber-900/30 text-amber-400" : "bg-amber-100 text-amber-700"} text-sm font-bold flex items-center gap-1.5`}>
            <Coins size={14} className="text-amber-500" />
            {userData?.user?.arthereumBalance?.toFixed(2)}
          </div>
        </div>

        {/* Impact Hero Card */}
        <div className={`
          rounded-3xl p-6 mb-6 relative overflow-hidden
          ${isDark 
            ? "bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border border-indigo-500/30" 
            : "bg-gradient-to-br from-indigo-500 to-purple-600 border border-indigo-400 shadow-lg text-white"}
        `}>
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <Video size={120} />
          </div>
          <div className="relative z-10">
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold mb-4 ${isDark ? "bg-indigo-500/20 text-indigo-300" : "bg-white/20 text-white"}`}>
              {currentTier.icon} {currentTier.name} Tier {currentTier.emoji}
            </div>
            <h2 className="text-2xl font-black mb-2 text-white">
              Watch Ads, Get Paid
            </h2>
            <p className={`text-sm mb-4 ${isDark ? "text-indigo-200" : "text-indigo-100"}`}>
              Earn up to <span className="font-black">{(getTotalEarned(DAILY_AD_LIMIT) + COMPLETION_BONUS).toFixed(1)} ATH</span> per day! Higher tiers = bigger rewards.
            </p>

            {/* Tier badges */}
            <div className="flex gap-2 mb-5 flex-wrap">
              {TIERS.map((tier, idx) => {
                const isActive = adsWatchedToday >= tier.range[0] - 1 && adsWatchedToday < tier.range[1];
                const isCompleted = adsWatchedToday >= tier.range[1];
                return (
                  <div key={idx} className={`px-2.5 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 ${
                    isCompleted
                      ? "bg-emerald-500/20 text-emerald-300"
                      : isActive
                      ? "bg-white/20 text-white ring-1 ring-white/40"
                      : "bg-white/5 text-white/40"
                  }`}>
                    {tier.emoji} {tier.reward} ATH
                  </div>
                );
              })}
            </div>

            {/* Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm font-bold">
                <span className={isDark ? "text-indigo-300" : "text-white"}>Daily Progress</span>
                <span className="text-white">
                  {adsWatchedToday} / {DAILY_AD_LIMIT} ads
                </span>
              </div>
              <div className={`h-3 rounded-full overflow-hidden ${isDark ? "bg-slate-800/50" : "bg-black/20"}`}>
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(adsWatchedToday / DAILY_AD_LIMIT) * 100}%` }}
                  className="h-full bg-gradient-to-r from-amber-400 to-yellow-300"
                />
              </div>
              <div className="flex justify-between text-xs font-medium opacity-80">
                <span>Earned: {totalEarned.toFixed(1)} ATH</span>
                <span>{isLimitReached ? "🎉 ALL DONE!" : `${totalPotential.toFixed(1)} ATH remaining`}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Completion bonus teaser */}
        {!isLimitReached && (
          <div className={`p-4 rounded-2xl border border-dashed mb-6 flex items-center justify-between ${
            isDark ? "bg-amber-500/5 border-amber-500/20" : "bg-amber-50 border-amber-300"
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? "bg-amber-500/20" : "bg-amber-100"}`}>
                <Trophy size={20} className="text-amber-500" />
              </div>
              <div>
                <p className={`text-sm font-black ${isDark ? "text-amber-300" : "text-amber-800"}`}>
                  Completion Bonus
                </p>
                <p className={`text-[10px] ${isDark ? "text-amber-400/60" : "text-amber-600"}`}>
                  Watch all {DAILY_AD_LIMIT} ads to earn extra
                </p>
              </div>
            </div>
            <span className={`font-black text-lg ${isDark ? "text-amber-300" : "text-amber-700"}`}>
              +{COMPLETION_BONUS} ATH
            </span>
          </div>
        )}

        {/* Main Action Area */}
        <div className={`p-6 rounded-3xl text-center shadow-lg border relative overflow-hidden ${
          isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-100"
        }`}>
          {/* Reward Animation overlay */}
          <AnimatePresence>
            {showRewardAnimation && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-20 flex items-center justify-center bg-emerald-500/90 backdrop-blur-sm"
              >
                <div className="text-center text-white">
                  <CheckCircle2 size={64} className="mx-auto mb-2 text-white" />
                  <h3 className="text-2xl font-black">+ {lastRewardAmount} ATH</h3>
                  <p className="text-sm font-bold opacity-90 mt-1">Reward Claimed!</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mb-6">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
              isLimitReached 
                ? (isDark ? "bg-slate-700" : "bg-gray-100") 
                : "bg-amber-100 text-amber-500 animate-pulse"
            }`}>
              {isLimitReached ? <Lock size={32} className="text-gray-400" /> : <Video size={36} fill="currentColor" />}
            </div>
            
            <h3 className={`text-xl font-black mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>
              {isLimitReached ? "Daily Limit Reached 🎉" : `${currentTier.name} Tier ${currentTier.emoji}`}
            </h3>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              {isLimitReached 
                ? `Amazing! You earned ${(totalEarned + COMPLETION_BONUS).toFixed(1)} ATH today!`
                : `Watch a short video to earn ${currentReward} ATH`
              }
            </p>
          </div>

          <button
            onClick={handleWatchAd}
            disabled={isWatchingAd || isLimitReached}
            className={`
              w-full py-4 rounded-2xl font-black text-lg flex items-center justify-center gap-2 transition-all
              ${isLimitReached 
                ? (isDark ? "bg-slate-700 text-slate-500 cursor-not-allowed" : "bg-gray-200 text-gray-400 cursor-not-allowed")
                : "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg hover:shadow-amber-500/30 active:scale-95"
              }
            `}
          >
            {isWatchingAd ? (
              <span className="animate-pulse">Loading Ad...</span>
            ) : isLimitReached ? (
              <>
                <CheckCircle2 size={20} /> All Done — Come Back Tomorrow!
              </>
            ) : (
              <>
                <Video size={20} /> Watch Ad — Earn {currentReward} ATH
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WatchAndEarnPage;
